import { defineComponent as _defineComponent } from 'vue';
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, unref as _unref, toDisplayString as _toDisplayString, normalizeStyle as _normalizeStyle, normalizeClass as _normalizeClass } from "vue";
const _hoisted_1 = { class: "layout-toolbar" };
const _hoisted_2 = {
    class: "card-drag-handle",
    "aria-label": "Drag to reorder"
};
const _hoisted_3 = {
    key: 0,
    class: "ytd-note"
};
const _hoisted_4 = { class: "dash-chart" };
const _hoisted_5 = { class: "chart-legend" };
const _hoisted_6 = {
    key: 0,
    class: "legend-item"
};
const _hoisted_7 = {
    key: 1,
    class: "legend-item"
};
const _hoisted_8 = {
    key: 1,
    class: "coverage"
};
const _hoisted_9 = { class: "coverage-text" };
const _hoisted_10 = {
    key: 0,
    class: "coverage-tick"
};
const _hoisted_11 = {
    key: 1,
    class: "coverage-last"
};
const _hoisted_12 = {
    key: 1,
    class: "dash-scroll breakdown-card"
};
const _hoisted_13 = { class: "view-payslip-wrap" };
const _hoisted_14 = { class: "payslip-list-dialog" };
const _hoisted_15 = { class: "chart-title" };
const _hoisted_16 = {
    key: 0,
    class: "payslip-list-employer"
};
const _hoisted_17 = { class: "tax-form-list" };
import { ref, computed, onMounted, getCurrentInstance, watch, nextTick, inject } from 'vue';
import { GridLayout, GridItem } from 'grid-layout-plus';
import { use } from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { BarChart } from 'echarts/charts';
import { GridComponent } from 'echarts/components';
import VChart from 'vue-echarts';
import { useAppState } from '@/composables/useAppState';
import { useCustomer } from '@/composables/useCustomer';
import { useWhatsApp } from '@/composables/useWhatsApp';
import { usePayslips } from '@/composables/usePayslips';
import { useTaxDocsSidebar } from '@/composables/useTaxDocsSidebar';
import SettingsOverlay from '../components/SettingsOverlay.vue';
import DocumentDialog from '../components/DocumentDialog.vue';
import MGButton from '@/components/elements/MGButton.vue';
import AiChatBanner from '@/components/elements/AiChatBanner.vue';
import WhatsAppEnableDrawer from '@/components/elements/WhatsAppEnableDrawer.vue';
import DocumentListItem from '@/components/elements/DocumentListItem.vue';
import PayslipBreakdown from '@/components/elements/PayslipBreakdown.vue';
import TaxCodeCard from '@/components/elements/TaxCodeCard.vue';
import { formatDate } from '@/utils/payslips';
const LAYOUT_KEY = 'payslips_dash_layout_v2';
export default /*@__PURE__*/ _defineComponent({
    __name: 'PayslipsPage',
    emits: ["colour-mode-changed"],
    setup(__props, { emit: __emit }) {
        use([CanvasRenderer, BarChart, GridComponent]);
        // Function ref: a string ref inside the GridItem v-for would collect an array.
        const chartRef = ref(null);
        function setChartRef(el) {
            chartRef.value = el;
        }
        const state = useAppState();
        const { getCustomer, savePreferences } = useCustomer();
        const instance = getCurrentInstance();
        const cookies = instance?.appContext.config.globalProperties.$cookies;
        const whatsAppNumber = instance?.appContext.config.globalProperties.$whatsAppNumber || '';
        const registerSidebar = inject('registerSidebar', null);
        const emit = __emit;
        // ---------- Desktop chrome ----------
        const colourMode = ref('light');
        const showSettings = ref(false);
        const customer = computed(() => state.customer);
        const safeTop = computed(() => parseInt(cookies?.get('st') ?? 0));
        const isDark = computed(() => colourMode.value === 'dark');
        function updatePref(key, value) {
            if (state.customer?.preferences) {
                state.customer.preferences[key] = value;
                savePreferences();
            }
        }
        function selectColourMode(mode) {
            colourMode.value = mode;
            localStorage.setItem('mg_colour_mode', mode);
            emit('colour-mode-changed', mode);
        }
        // ---------- Document dialog ----------
        const docDialog = ref(null);
        const selectedDocId = ref('');
        function openDoc(id) {
            selectedDocId.value = id;
            // Wait for the documentId prop to propagate before the dialog reads it.
            nextTick(() => docDialog.value?.openDialog());
        }
        // ---------- WhatsApp ----------
        const whatsAppPromoCard = ref(null);
        const whatsAppEnableDrawer = ref(null);
        const { handleWhatsAppAction, handleEnableWhatsApp, handleWhatsAppEnableDone, handleWhatsAppDrawerClosed } = useWhatsApp({
            whatsAppPromoCard,
            whatsAppEnableDrawer,
            whatsAppNumber,
        });
        // ---------- Payslips (shared with the mobile page) ----------
        const { loaded, selectedMonth, selectedPayslip, selectedMonthName, selectedMonthOnly, selectedTaxYear, selectedTaxCode, selectedEstimate, monthOnMonthText, yearToDateNote, coverage, coverageText, lastPayslipText, monthNameOf, legendSeries, hasMissingMonths, hasEstimates, chartOption, onChartClick, payslipButtonText, selectedMonthByEmployer, hmrcByYear, aiContextFor, aiPromptsFor, loadPayslips, } = usePayslips({ isDark: () => isDark.value });
        // Desktop shows pay and tax forms side by side, so the chat gets both.
        const aiContext = computed(() => [aiContextFor('pay'), aiContextFor('tax')].filter(Boolean).join('. '));
        const aiPrompts = computed(() => [...aiPromptsFor('pay'), ...aiPromptsFor('tax')]);
        const taxCodeOpen = ref(false);
        const payslipListOpen = ref(false);
        const chatBanner = ref(null);
        function askAboutTaxCode(question) {
            taxCodeOpen.value = false;
            chatBanner.value?.openChat(question);
        }
        // Desktop has room for every month, so the £ axis sits inside the chart instead of pinned beside a scroller.
        const desktopChartOption = computed(() => {
            const option = chartOption.value;
            return {
                ...option,
                grid: { ...option.grid, left: 44 },
                yAxis: { ...option.yAxis, axisLabel: { ...option.yAxis.axisLabel, show: true } },
            };
        });
        function onZrClick(event) {
            onChartClick(chartRef.value, event);
        }
        // "Add" uses the WhatsApp flow until in-app upload (DEV-144) exists.
        function onPayslipButton() {
            const list = selectedMonth.value?.payslips || [];
            if (!list.length)
                handleWhatsAppAction();
            else if (list.length > 1)
                payslipListOpen.value = true;
            else
                openDoc(list[0].original.id);
        }
        function openPayslip(id) {
            payslipListOpen.value = false;
            openDoc(id);
        }
        // ---------- Dashboard layout (grid-layout-plus: draggable + resizable cards) ----------
        // v2: default changed to chart full width with the breakdown full width below it.
        const ALL_CARDS = ['chart', 'breakdown'];
        const DEFAULT_LAYOUT = {
            chart: { x: 0, y: 0, w: 12, h: 15 },
            breakdown: { x: 0, y: 15, w: 12, h: 20 },
        };
        function defaultPositions() {
            const o = {};
            for (const id of ALL_CARDS)
                o[id] = { ...DEFAULT_LAYOUT[id] };
            return o;
        }
        function loadPositions() {
            const pos = defaultPositions();
            try {
                const saved = JSON.parse(localStorage.getItem(LAYOUT_KEY) || 'null');
                if (saved && typeof saved === 'object') {
                    for (const id of ALL_CARDS)
                        if (saved[id])
                            pos[id] = { ...pos[id], ...saved[id] };
                }
            }
            catch { /* ignore malformed */ }
            return pos;
        }
        const positions = ref(loadPositions());
        const layout = ref([]);
        const availableCards = computed(() => {
            const ids = ['chart'];
            if (selectedMonth.value)
                ids.push('breakdown');
            return ids;
        });
        function rebuildLayout() {
            layout.value = availableCards.value.map(id => ({ i: id, ...positions.value[id] }));
        }
        // Persist positions whenever grid-layout-plus moves/resizes/compacts a card.
        function onLayoutUpdated(updated) {
            for (const it of updated)
                positions.value[it.i] = { x: it.x, y: it.y, w: it.w, h: it.h };
            localStorage.setItem(LAYOUT_KEY, JSON.stringify(positions.value));
        }
        function resetLayout() {
            positions.value = defaultPositions();
            localStorage.removeItem(LAYOUT_KEY);
            rebuildLayout();
        }
        watch(() => availableCards.value.join(), rebuildLayout, { immediate: true });
        // ---------- Sidebar: tax docs by tax year (re-registered as the documents load) ----------
        const { registerTaxDocsSidebar } = useTaxDocsSidebar(registerSidebar);
        watch(hmrcByYear, groups => registerTaxDocsSidebar(groups, {
            onOpenDoc: openDoc,
            onAdd: handleWhatsAppAction,
            onOpenSettings: () => { showSettings.value = true; },
        }), { immediate: true });
        onMounted(() => {
            colourMode.value = localStorage.getItem('mg_colour_mode') || 'light';
            getCustomer(() => { });
            loadPayslips();
        });
        return (_ctx, _cache) => {
            const _component_v_icon = _resolveComponent("v-icon");
            const _component_v_progress_linear = _resolveComponent("v-progress-linear");
            const _component_v_skeleton_loader = _resolveComponent("v-skeleton-loader");
            const _component_v_dialog = _resolveComponent("v-dialog");
            return (_openBlock(), _createElementBlock("div", {
                class: _normalizeClass(["desktop-payslips-page", colourMode.value])
            }, [
                (showSettings.value)
                    ? (_openBlock(), _createBlock(SettingsOverlay, {
                        key: 0,
                        customer: customer.value,
                        colourMode: colourMode.value,
                        safeTop: safeTop.value,
                        onClose: _cache[0] || (_cache[0] = ($event) => (showSettings.value = false)),
                        onUpdatePref: updatePref,
                        onSelectColourMode: selectColourMode
                    }, null, 8, ["customer", "colourMode", "safeTop"]))
                    : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                        _createVNode(AiChatBanner, {
                            ref_key: "chatBanner",
                            ref: chatBanner,
                            prompts: aiPrompts.value,
                            dynamicSuggestions: true,
                            context: aiContext.value,
                            prefill: true
                        }, null, 8, ["prompts", "context"]),
                        _createElementVNode("div", _hoisted_1, [
                            _createElementVNode("button", {
                                class: "reset-layout-btn",
                                onClick: resetLayout
                            }, [
                                _createVNode(_component_v_icon, { size: "16" }, {
                                    default: _withCtx(() => _cache[5] || (_cache[5] = [
                                        _createTextVNode("mdi-restore")
                                    ])),
                                    _: 1
                                }),
                                _cache[6] || (_cache[6] = _createElementVNode("span", null, "Reset layout", -1))
                            ])
                        ]),
                        _createVNode(_unref(GridLayout), {
                            layout: layout.value,
                            "onUpdate:layout": _cache[2] || (_cache[2] = ($event) => ((layout).value = $event)),
                            "col-num": 12,
                            "row-height": 10,
                            margin: [20, 20],
                            "is-draggable": true,
                            "is-resizable": true,
                            "vertical-compact": true,
                            onLayoutUpdated: onLayoutUpdated
                        }, {
                            default: _withCtx(() => [
                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(layout.value, (item) => {
                                    return (_openBlock(), _createBlock(_unref(GridItem), {
                                        key: item.i,
                                        x: item.x,
                                        y: item.y,
                                        w: item.w,
                                        h: item.h,
                                        i: item.i,
                                        "drag-allow-from": ".card-drag-handle",
                                        "drag-ignore-from": ""
                                    }, {
                                        default: _withCtx(() => [
                                            _createElementVNode("div", {
                                                class: _normalizeClass(["chart-card", colourMode.value])
                                            }, [
                                                _createElementVNode("div", _hoisted_2, [
                                                    _createVNode(_component_v_icon, { size: "18" }, {
                                                        default: _withCtx(() => _cache[7] || (_cache[7] = [
                                                            _createTextVNode("mdi-drag")
                                                        ])),
                                                        _: 1
                                                    })
                                                ]),
                                                (item.i === 'chart')
                                                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                                        _cache[10] || (_cache[10] = _createElementVNode("h3", { class: "chart-title" }, "Pay history", -1)),
                                                        (_unref(loaded))
                                                            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                                                (_unref(yearToDateNote))
                                                                    ? (_openBlock(), _createElementBlock("div", _hoisted_3, _toDisplayString(_unref(yearToDateNote)), 1))
                                                                    : _createCommentVNode("", true),
                                                                _createElementVNode("div", _hoisted_4, [
                                                                    _createVNode(_unref(VChart), {
                                                                        ref_for: true,
                                                                        ref: setChartRef,
                                                                        option: desktopChartOption.value,
                                                                        style: { height: '100%', width: '100%' },
                                                                        autoresize: "",
                                                                        "onZr:click": onZrClick
                                                                    }, null, 8, ["option"])
                                                                ]),
                                                                _createElementVNode("div", _hoisted_5, [
                                                                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(legendSeries), (s) => {
                                                                        return (_openBlock(), _createElementBlock("span", {
                                                                            key: s.key,
                                                                            class: "legend-item"
                                                                        }, [
                                                                            _createElementVNode("span", {
                                                                                class: "legend-swatch",
                                                                                style: _normalizeStyle({ background: `var(${s.cssVar})` })
                                                                            }, null, 4),
                                                                            _createTextVNode(_toDisplayString(s.label), 1)
                                                                        ]));
                                                                    }), 128)),
                                                                    (_unref(hasEstimates))
                                                                        ? (_openBlock(), _createElementBlock("span", _hoisted_6, _cache[8] || (_cache[8] = [
                                                                            _createElementVNode("span", { class: "legend-swatch legend-swatch--estimated" }, null, -1),
                                                                            _createTextVNode("Est. ")
                                                                        ])))
                                                                        : _createCommentVNode("", true),
                                                                    (_unref(hasMissingMonths))
                                                                        ? (_openBlock(), _createElementBlock("span", _hoisted_7, _cache[9] || (_cache[9] = [
                                                                            _createElementVNode("span", { class: "legend-swatch legend-swatch--missing" }, null, -1),
                                                                            _createTextVNode("Missing ")
                                                                        ])))
                                                                        : _createCommentVNode("", true)
                                                                ]),
                                                                (_unref(coverage))
                                                                    ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                                                                        _createElementVNode("span", _hoisted_9, [
                                                                            _createTextVNode(_toDisplayString(_unref(coverageText)), 1),
                                                                            (_unref(coverage).held >= _unref(coverage).expected)
                                                                                ? (_openBlock(), _createElementBlock("span", _hoisted_10, " ✓"))
                                                                                : _createCommentVNode("", true)
                                                                        ]),
                                                                        (_unref(coverage).held < _unref(coverage).expected)
                                                                            ? (_openBlock(), _createBlock(_component_v_progress_linear, {
                                                                                key: 0,
                                                                                class: "coverage-bar",
                                                                                "model-value": _unref(coverage).expected ? (_unref(coverage).held / _unref(coverage).expected) * 100 : 100,
                                                                                height: "4",
                                                                                rounded: ""
                                                                            }, null, 8, ["model-value"]))
                                                                            : _createCommentVNode("", true),
                                                                        (_unref(lastPayslipText))
                                                                            ? (_openBlock(), _createElementBlock("div", _hoisted_11, _toDisplayString(_unref(lastPayslipText)), 1))
                                                                            : _createCommentVNode("", true)
                                                                    ]))
                                                                    : _createCommentVNode("", true)
                                                            ], 64))
                                                            : (_openBlock(), _createBlock(_component_v_skeleton_loader, {
                                                                key: 1,
                                                                type: "card",
                                                                loading: true
                                                            }))
                                                    ], 64))
                                                    : (item.i === 'breakdown')
                                                        ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
                                                            _createVNode(PayslipBreakdown, {
                                                                payslip: _unref(selectedPayslip),
                                                                monthName: _unref(selectedMonthName),
                                                                monthOnly: _unref(selectedMonthOnly),
                                                                payslipCount: _unref(selectedMonth).payslips.length,
                                                                estimate: _unref(selectedEstimate),
                                                                estimateSource: _unref(selectedEstimate) ? _unref(monthNameOf)(_unref(selectedEstimate).sourceKey) : '',
                                                                monthOnMonth: _unref(monthOnMonthText),
                                                                taxCodeVariant: _unref(selectedTaxCode)?.variant,
                                                                dark: isDark.value,
                                                                onTaxCode: _cache[1] || (_cache[1] = ($event) => (taxCodeOpen.value = true))
                                                            }, null, 8, ["payslip", "monthName", "monthOnly", "payslipCount", "estimate", "estimateSource", "monthOnMonth", "taxCodeVariant", "dark"]),
                                                            _createElementVNode("div", _hoisted_13, [
                                                                _createVNode(MGButton, {
                                                                    text: _unref(payslipButtonText),
                                                                    fullWidth: true,
                                                                    arrow: true,
                                                                    onClick: onPayslipButton
                                                                }, null, 8, ["text"])
                                                            ])
                                                        ]))
                                                        : _createCommentVNode("", true)
                                            ], 2)
                                        ]),
                                        _: 2
                                    }, 1032, ["x", "y", "w", "h", "i"]));
                                }), 128))
                            ]),
                            _: 1
                        }, 8, ["layout"])
                    ], 64)),
                _createVNode(_component_v_dialog, {
                    modelValue: taxCodeOpen.value,
                    "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event) => ((taxCodeOpen).value = $event)),
                    "max-width": "480",
                    attach: ""
                }, {
                    default: _withCtx(() => [
                        (_unref(selectedTaxCode))
                            ? (_openBlock(), _createBlock(TaxCodeCard, {
                                key: 0,
                                code: _unref(selectedPayslip).taxCode,
                                taxYear: _unref(selectedTaxYear),
                                onAsk: askAboutTaxCode
                            }, null, 8, ["code", "taxYear"]))
                            : _createCommentVNode("", true)
                    ]),
                    _: 1
                }, 8, ["modelValue"]),
                _createVNode(_component_v_dialog, {
                    modelValue: payslipListOpen.value,
                    "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event) => ((payslipListOpen).value = $event)),
                    "max-width": "480",
                    attach: ""
                }, {
                    default: _withCtx(() => [
                        _createElementVNode("div", _hoisted_14, [
                            _createElementVNode("h3", _hoisted_15, _toDisplayString(_unref(selectedMonthOnly)) + " payslips", 1),
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(selectedMonthByEmployer), (group) => {
                                return (_openBlock(), _createElementBlock("div", {
                                    key: group.employer,
                                    class: "payslip-list-group"
                                }, [
                                    (_unref(selectedMonthByEmployer).length > 1)
                                        ? (_openBlock(), _createElementBlock("h3", _hoisted_16, _toDisplayString(group.employer), 1))
                                        : _createCommentVNode("", true),
                                    _createElementVNode("div", _hoisted_17, [
                                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(group.payslips, (p) => {
                                            return (_openBlock(), _createBlock(DocumentListItem, {
                                                key: p.original.id,
                                                company: p.employer,
                                                fileName: `Net pay ${_unref(state).formatter.format(p.netPay)}`,
                                                datePrefix: "Paid",
                                                date: p.datePaid ? _unref(formatDate)(p.datePaid) : '',
                                                onClick: ($event) => (openPayslip(p.original.id))
                                            }, null, 8, ["company", "fileName", "date", "onClick"]));
                                        }), 128))
                                    ])
                                ]));
                            }), 128))
                        ])
                    ]),
                    _: 1
                }, 8, ["modelValue"]),
                _createVNode(WhatsAppEnableDrawer, {
                    ref_key: "whatsAppEnableDrawer",
                    ref: whatsAppEnableDrawer,
                    phoneNumber: _unref(state).customer?.person?.mobilePhone || '',
                    onEnable: _unref(handleEnableWhatsApp),
                    onDone: _unref(handleWhatsAppEnableDone),
                    onClosed: _unref(handleWhatsAppDrawerClosed)
                }, null, 8, ["phoneNumber", "onEnable", "onDone", "onClosed"]),
                _createVNode(DocumentDialog, {
                    ref_key: "docDialog",
                    ref: docDialog,
                    documentId: selectedDocId.value
                }, null, 8, ["documentId"])
            ], 2));
        };
    }
});
